/**
 * AdminPanel.js - Admin Dashboard for QuickyGame
 * Handles user management, stats visualization, and admin actions.
 */
export default class AdminPanel {
    constructor(engine) {
        this.engine = engine;
        this.users = [];
        this.stats = null;
        this.currentTab = 'users';
        this.searchTerm = '';
        this.currentPage = 1;
        this.perPage = 20;
    }

    get baseUrl() {
        return this.engine.authManager.db.baseUrl;
    }

    get token() {
        return this.engine.authManager.db.token;
    }

    async init() {
        // Tab switching
        document.getElementById('admin-tab-users').addEventListener('click', () => this.switchTab('users'));
        document.getElementById('admin-tab-stats').addEventListener('click', () => this.switchTab('stats'));

        // Search
        const searchInput = document.getElementById('admin-search');
        if (searchInput) {
            searchInput.addEventListener('input', (e) => {
                this.searchTerm = e.target.value.toLowerCase();
                this.currentPage = 1;
                this.renderUsers();
            });
        }

        // Back button
        document.getElementById('admin-back-btn').addEventListener('click', () => {
            this.engine.hideAllScreens();
            document.getElementById('main-menu').classList.remove('hidden');
            document.getElementById('main-menu').classList.add('active');
        });
    }

    switchTab(tab) {
        this.currentTab = tab;

        // Update tab buttons
        document.querySelectorAll('.admin-tab').forEach(t => t.classList.remove('active'));
        document.querySelector(`[data-tab="${tab}"]`).classList.add('active');

        // Toggle panels
        document.getElementById('admin-users-panel').classList.toggle('hidden', tab !== 'users');
        document.getElementById('admin-stats-panel').classList.toggle('hidden', tab !== 'stats');

        if (tab === 'stats' && !this.stats) {
            this.loadStats();
        }
    }

    async show() {
        this.engine.hideAllScreens();
        document.getElementById('admin-screen').classList.remove('hidden');
        document.getElementById('admin-screen').classList.add('active');

        await this.loadUsers();
        if (this.currentTab === 'stats') {
            await this.loadStats();
        }
    }

    // ===================== USERS =====================

    async loadUsers() {
        try {
            const res = await fetch(`${this.baseUrl}/admin/users`, {
                headers: { 'Authorization': `Bearer ${this.token}` }
            });
            const data = await res.json();
            if (Array.isArray(data)) {
                this.users = data;
                this.renderUsers();
            }
        } catch (err) {
            console.error('[AdminPanel] Failed to load users:', err);
        }
    }

    renderUsers() {
        const container = document.getElementById('admin-users-list');
        const countEl = document.getElementById('admin-user-count');

        // Filter
        let filtered = this.users;
        if (this.searchTerm) {
            filtered = this.users.filter(u =>
                u.username.toLowerCase().includes(this.searchTerm) ||
                (u.friend_code || '').toLowerCase().includes(this.searchTerm)
            );
        }

        // Sort by level descending
        filtered.sort((a, b) => (b.level || 0) - (a.level || 0));

        // Pagination
        const totalPages = Math.max(1, Math.ceil(filtered.length / this.perPage));
        if (this.currentPage > totalPages) this.currentPage = totalPages;
        const start = (this.currentPage - 1) * this.perPage;
        const pageUsers = filtered.slice(start, start + this.perPage);

        countEl.textContent = `${filtered.length} / ${this.users.length} users`;

        if (filtered.length === 0) {
            container.innerHTML = `<div style="text-align:center; color:#666; padding:30px;">No users found.</div>`;
            return;
        }

        // Gift All button bar
        let html = `<div class="admin-gift-all-bar">
            <button class="admin-gift-all-btn" onclick="window._adminPanel.openGiftModal('all', 'ALL USERS')">🎁 GIFT ALL USERS</button>
        </div>`;

        html += pageUsers.map(user => {
            const isBlocked = user.is_blocked;
            const isAdmin = user.is_admin;
            const isPremium = user.is_premium;
            const initial = (user.username || '?')[0].toUpperCase();
            const lastLogin = user.last_login
                ? this.formatDate(user.last_login)
                : 'Never';
            const createdAt = this.formatDate(user.created_at);

            return `
                <div class="admin-user-card ${isBlocked ? 'blocked' : ''}" data-user-id="${user.id}">
                    <div class="admin-user-avatar">${initial}</div>
                    <div class="admin-user-info">
                        <div class="admin-user-name">
                            ${user.username}
                            ${isAdmin ? '<span class="admin-badge">ADMIN</span>' : ''}
                            ${isBlocked ? '<span class="blocked-badge">BLOCKED</span>' : ''}
                            ${isPremium ? '<span class="premium-badge">PREMIUM</span>' : ''}
                        </div>
                        <div class="admin-user-meta">
                            Last: ${lastLogin} · Since: ${createdAt}
                        </div>
                    </div>
                    <div class="admin-user-stats">
                        <div class="admin-user-stat">
                            <span class="stat-val">${user.level}</span>
                            LVL
                        </div>
                        <div class="admin-user-stat">
                            <span class="stat-val">${user.coins}</span>
                            💰
                        </div>
                    </div>
                    <div class="admin-user-actions">
                        <button class="admin-action-btn premium" 
                                onclick="window._adminPanel.togglePremium(${user.id}, ${!isPremium})" 
                                title="${isPremium ? 'Remove Premium' : 'Give Premium'}">
                            ⭐
                        </button>
                        <button class="admin-action-btn gift" 
                                onclick="window._adminPanel.openGiftModal(${user.id}, '${user.username.replace(/'/g, "\\'")}')" 
                                title="Gift coins">
                            🎁
                        </button>
                        ${!isAdmin ? `
                            <button class="admin-action-btn ${isBlocked ? 'unblock' : ''}" 
                                    onclick="window._adminPanel.toggleBlock(${user.id}, ${!isBlocked})" 
                                    title="${isBlocked ? 'Unblock' : 'Block'}">
                                ${isBlocked ? '✓' : '🚫'}
                            </button>
                            <button class="admin-action-btn danger" 
                                    onclick="window._adminPanel.confirmDelete(${user.id}, '${user.username.replace(/'/g, "\\'")}')" 
                                    title="Delete">
                                🗑
                            </button>
                        ` : ''}
                    </div>
                </div>
            `;
        }).join('');

        // Pagination controls
        if (totalPages > 1) {
            html += `
                <div class="admin-pagination">
                    <button class="admin-pagination-btn" ${this.currentPage <= 1 ? 'disabled' : ''}
                            onclick="window._adminPanel.goToPage(${this.currentPage - 1})">◄ PREV</button>
                    <span class="admin-pagination-info">${this.currentPage} / ${totalPages}</span>
                    <button class="admin-pagination-btn" ${this.currentPage >= totalPages ? 'disabled' : ''}
                            onclick="window._adminPanel.goToPage(${this.currentPage + 1})">NEXT ►</button>
                </div>
            `;
        }

        container.innerHTML = html;
    }

    goToPage(page) {
        this.currentPage = page;
        this.renderUsers();
        // Scroll admin screen to top
        const adminScreen = document.getElementById('admin-screen');
        if (adminScreen) adminScreen.scrollTop = 0;
    }

    async toggleBlock(userId, block) {
        try {
            const res = await fetch(`${this.baseUrl}/admin/block`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${this.token}`
                },
                body: JSON.stringify({ userId, blocked: block })
            });

            const data = await res.json();
            if (data.success) {
                // Update local data
                const user = this.users.find(u => u.id === userId);
                if (user) user.is_blocked = block;
                this.renderUsers();
                this.engine.showNotification(block ? 'USER BLOCKED 🚫' : 'USER UNBLOCKED ✓', 2000);
            }
        } catch (err) {
            console.error('[AdminPanel] Block failed:', err);
        }
    }

    async togglePremium(userId, premium) {
        if (!confirm(`Are you sure you want to ${premium ? 'GIVE' : 'REMOVE'} premium status?`)) return;

        try {
            const res = await fetch(`${this.baseUrl}/admin/premium`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${this.token}`
                },
                body: JSON.stringify({ userId, premium })
            });

            const data = await res.json();
            if (data.success) {
                const user = this.users.find(u => u.id === userId);
                if (user) user.is_premium = premium;
                this.renderUsers();
                this.engine.showNotification(`User ${premium ? 'UPGRADED' : 'DOWNGRADED'}!`, 2000);
            } else {
                this.engine.showNotification(data.message || 'Error', 2000);
            }
        } catch (err) {
            console.error('Premium toggle error:', err);
        }
    }

    confirmDelete(userId, username) {
        // Show custom confirm dialog
        const overlay = document.createElement('div');
        overlay.className = 'admin-confirm-overlay';
        overlay.innerHTML = `
            <div class="admin-confirm-box">
                <p>⚠ DELETE <span style="color:#ff3333; font-weight:bold;">${username}</span>?<br>
                <span style="font-size:0.8rem; color:#888;">This action is irreversible.</span></p>
                <div class="admin-confirm-actions">
                    <button class="neon-btn small" id="admin-confirm-yes" style="border-color:#ff0055; color:#ff0055;">DELETE</button>
                    <button class="neon-btn small" id="admin-confirm-no">CANCEL</button>
                </div>
            </div>
        `;
        document.body.appendChild(overlay);

        document.getElementById('admin-confirm-yes').addEventListener('click', async () => {
            overlay.remove();
            await this.deleteUser(userId);
        });

        document.getElementById('admin-confirm-no').addEventListener('click', () => {
            overlay.remove();
        });
    }

    async deleteUser(userId) {
        try {
            const res = await fetch(`${this.baseUrl}/admin/user/${userId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${this.token}` }
            });

            const data = await res.json();
            if (data.success) {
                this.users = this.users.filter(u => u.id !== userId);
                this.renderUsers();
                this.engine.showNotification('USER DELETED 🗑', 2000);
            }
        } catch (err) {
            console.error('[AdminPanel] Delete failed:', err);
        }
    }

    // ===================== GIFTS =====================

    openGiftModal(userId, username) {
        const overlay = document.createElement('div');
        overlay.className = 'admin-gift-overlay';
        overlay.innerHTML = `
            <div class="admin-gift-box">
                <h3>🎁 SEND GIFT</h3>
                <div class="admin-gift-recipient">To: <strong>${username}</strong></div>
                <input type="number" id="gift-coins-input" class="admin-gift-input" placeholder="Coins amount" min="1" value="100">
                <textarea id="gift-message-input" class="admin-gift-input admin-gift-textarea" placeholder="Message for the player..."></textarea>
                <div class="admin-gift-actions">
                    <button class="neon-btn small" id="gift-send-btn" style="border-color: var(--primary-color); color: var(--primary-color);">SEND 🎁</button>
                    <button class="neon-btn small" id="gift-cancel-btn" style="border-color: var(--secondary-color); color: var(--secondary-color);">CANCEL</button>
                </div>
            </div>
        `;
        document.body.appendChild(overlay);

        document.getElementById('gift-send-btn').addEventListener('click', async () => {
            const coins = parseInt(document.getElementById('gift-coins-input').value);
            const message = document.getElementById('gift-message-input').value;

            if (!coins || coins <= 0) {
                this.engine.showNotification('Enter a valid coin amount!', 2000);
                return;
            }
            if (!message || !message.trim()) {
                this.engine.showNotification('Enter a message!', 2000);
                return;
            }

            overlay.remove();
            await this.sendGift(userId, coins, message);
        });

        document.getElementById('gift-cancel-btn').addEventListener('click', () => {
            overlay.remove();
        });
    }

    async sendGift(userId, coins, message) {
        try {
            const res = await fetch(`${this.baseUrl}/admin/gift`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${this.token}`
                },
                body: JSON.stringify({ userId, coins, message })
            });

            const data = await res.json();
            if (data.success) {
                this.engine.showNotification(`🎁 ${coins} coins sent to ${data.recipients} user(s)!`, 3000);
                // Refresh user list to show updated balances
                await this.loadUsers();
            } else {
                this.engine.showNotification('Gift failed: ' + (data.message || 'Unknown error'), 3000);
            }
        } catch (err) {
            console.error('[AdminPanel] Gift failed:', err);
            this.engine.showNotification('Gift failed!', 2000);
        }
    }

    // ===================== STATS =====================

    async loadStats() {
        try {
            const res = await fetch(`${this.baseUrl}/admin/stats`, {
                headers: { 'Authorization': `Bearer ${this.token}` }
            });
            this.stats = await res.json();
            this.renderStats();
        } catch (err) {
            console.error('[AdminPanel] Failed to load stats:', err);
        }
    }

    renderStats() {
        if (!this.stats) return;
        const s = this.stats;

        // Summary Cards
        const cardsEl = document.getElementById('admin-stats-cards');
        cardsEl.innerHTML = `
            <div class="admin-stat-card">
                <span class="admin-stat-value">${s.totalUsers}</span>
                <span class="admin-stat-label">Total Users</span>
            </div>
            <div class="admin-stat-card">
                <span class="admin-stat-value">${s.activeUsers}</span>
                <span class="admin-stat-label">Active (7d)</span>
            </div>
            <div class="admin-stat-card">
                <span class="admin-stat-value">${s.activeToday}</span>
                <span class="admin-stat-label">Active Today</span>
            </div>
            <div class="admin-stat-card">
                <span class="admin-stat-value">${s.newUsersWeek}</span>
                <span class="admin-stat-label">New (7d)</span>
            </div>
            <div class="admin-stat-card">
                <span class="admin-stat-value">${s.totalGames}</span>
                <span class="admin-stat-label">Games Played</span>
            </div>
            <div class="admin-stat-card">
                <span class="admin-stat-value">${s.gamesToday}</span>
                <span class="admin-stat-label">Games Today</span>
            </div>
            <div class="admin-stat-card">
                <span class="admin-stat-value" style="color: #ff0055; text-shadow: 0 0 10px #ff0055;">${s.blockedUsers}</span>
                <span class="admin-stat-label">Blocked</span>
            </div>
            <div class="admin-stat-card">
                <span class="admin-stat-value" style="color: var(--accent-color); text-shadow: 0 0 10px var(--accent-color);">${s.avgLevel}</span>
                <span class="admin-stat-label">Avg Level</span>
            </div>
        `;

        // Charts (Canvas-based)
        this.drawLineChart('admin-growth-chart', s.growth, 'new_users', 'date', '#ff3333');
        this.drawLineChart('admin-activity-chart', s.activity, 'games_played', 'date', '#00f3ff');

        // Top Games Bar
        this.renderTopGames(s.topGames);

        // Level Distribution
        this.renderLevelDist(s.levelDistribution);
    }

    drawLineChart(canvasId, data, valueKey, labelKey, color) {
        const canvas = document.getElementById(canvasId);
        if (!canvas || !data || data.length === 0) return;

        const ctx = canvas.getContext('2d');
        const w = canvas.width = canvas.parentElement.clientWidth - 30;
        const h = canvas.height = 160;

        ctx.clearRect(0, 0, w, h);

        const values = data.map(d => d[valueKey] || 0);
        const maxVal = Math.max(...values, 1);
        const padding = { top: 10, bottom: 25, left: 35, right: 10 };
        const chartW = w - padding.left - padding.right;
        const chartH = h - padding.top - padding.bottom;

        // Grid lines
        ctx.strokeStyle = '#222';
        ctx.lineWidth = 1;
        for (let i = 0; i <= 4; i++) {
            const y = padding.top + (chartH / 4) * i;
            ctx.beginPath();
            ctx.moveTo(padding.left, y);
            ctx.lineTo(w - padding.right, y);
            ctx.stroke();

            // Y labels
            ctx.fillStyle = '#666';
            ctx.font = '10px monospace';
            ctx.textAlign = 'right';
            const label = Math.round(maxVal * (1 - i / 4));
            ctx.fillText(label, padding.left - 5, y + 4);
        }

        if (values.length < 2) return;

        // Line
        ctx.beginPath();
        ctx.strokeStyle = color;
        ctx.lineWidth = 2;
        ctx.shadowColor = color;
        ctx.shadowBlur = 8;

        const stepX = chartW / (values.length - 1);
        values.forEach((val, i) => {
            const x = padding.left + i * stepX;
            const y = padding.top + chartH - (val / maxVal) * chartH;
            if (i === 0) ctx.moveTo(x, y);
            else ctx.lineTo(x, y);
        });
        ctx.stroke();

        // Fill area under line
        ctx.shadowBlur = 0;
        ctx.lineTo(padding.left + (values.length - 1) * stepX, padding.top + chartH);
        ctx.lineTo(padding.left, padding.top + chartH);
        ctx.closePath();
        const grad = ctx.createLinearGradient(0, padding.top, 0, h);
        grad.addColorStop(0, color + '33');
        grad.addColorStop(1, color + '00');
        ctx.fillStyle = grad;
        ctx.fill();

        // Dots
        ctx.shadowColor = color;
        ctx.shadowBlur = 6;
        values.forEach((val, i) => {
            const x = padding.left + i * stepX;
            const y = padding.top + chartH - (val / maxVal) * chartH;
            ctx.beginPath();
            ctx.arc(x, y, 3, 0, Math.PI * 2);
            ctx.fillStyle = color;
            ctx.fill();
        });
        ctx.shadowBlur = 0;

        // X labels (show first, mid, last)
        ctx.fillStyle = '#666';
        ctx.font = '9px monospace';
        ctx.textAlign = 'center';
        const showIndices = [0, Math.floor(data.length / 2), data.length - 1];
        showIndices.forEach(i => {
            if (data[i]) {
                const x = padding.left + i * stepX;
                const dateStr = new Date(data[i][labelKey]).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
                ctx.fillText(dateStr, x, h - 5);
            }
        });
    }

    renderTopGames(topGames) {
        const el = document.getElementById('admin-top-games');
        if (!el || !topGames || topGames.length === 0) {
            if (el) el.innerHTML = '<span style="color:#666;">No data</span>';
            return;
        }

        const maxCount = topGames[0].play_count || 1;
        el.innerHTML = topGames.map(g => {
            const pct = Math.round((g.play_count / maxCount) * 100);
            return `
                <div class="admin-game-bar">
                    <span class="admin-game-bar-label">${g.game_key}</span>
                    <div class="admin-game-bar-track">
                        <div class="admin-game-bar-fill" style="width: ${pct}%">${g.play_count}</div>
                    </div>
                </div>
            `;
        }).join('');
    }

    renderLevelDist(dist) {
        const el = document.getElementById('admin-level-dist');
        if (!el || !dist || dist.length === 0) {
            if (el) el.innerHTML = '<span style="color:#666;">No data</span>';
            return;
        }

        const maxCount = Math.max(...dist.map(d => d.count), 1);
        el.innerHTML = dist.map(d => {
            const pct = Math.round((d.count / maxCount) * 100);
            return `
                <div class="admin-level-bar">
                    <span class="admin-level-bar-label">LVL ${d.range}</span>
                    <div class="admin-level-bar-track">
                        <div class="admin-level-bar-fill" style="width: ${pct}%">${d.count}</div>
                    </div>
                </div>
            `;
        }).join('');
    }

    // ===================== HELPERS =====================

    formatDate(dateStr) {
        if (!dateStr) return '—';
        const d = new Date(dateStr);
        const now = new Date();
        const diff = now - d;

        // Less than 1 hour
        if (diff < 3600000) {
            const mins = Math.floor(diff / 60000);
            return `${mins}m ago`;
        }
        // Less than 24h
        if (diff < 86400000) {
            const hours = Math.floor(diff / 3600000);
            return `${hours}h ago`;
        }
        // Less than 7 days
        if (diff < 604800000) {
            const days = Math.floor(diff / 86400000);
            return `${days}d ago`;
        }
        // Older
        return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' });
    }
}
